// The Forge inside a rock (components/ForgeRockMilestones.vue, on RockModal.vue): what is sent to
// POST /api/ai/team-draft with kind 'milestones' (API services/aiteam.ts RULES.milestones), in the same shape the
// Forge's Milestones action on the Scoreboard sends (ForgeTeam.vue) but for the one rock that is open, and the
// draft checked against that rock before its milestones go onto the form. Nothing here saves anything.
import { teamLabel } from '@/scoreboard';
import { quarterBounds, today } from '@/rocks';

export const validDate = (s: any): boolean => typeof s == 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s + 'T00:00:00Z'));

// New milestones can't be due before today, and stay inside the quarter
export function earliestDue(quarter: string, todayStr: string = today()): string {
    const b = quarterBounds(quarter);
    return todayStr < b.start ? b.start : todayStr > b.end ? b.end : todayStr;
}

// rockID is 0 for a rock that isn't saved yet; form is RockModal's form (Name, DoneStatement, OwnerEmployeeID, Team,
// TeamRock, Milestones [{ Text, OwnerEmployeeID, DueDate, Done }])
export function milestoneDraftContext(rockID: number, form: any, quarter: string, employees: any[], todayStr: string = today()): any {
    const b = quarterBounds(quarter);
    return {
        team: form.Team ? teamLabel(form.Team) : '', quarter, quarterStart: b.start, quarterEnd: b.end, today: todayStr, earliestDate: earliestDue(quarter, todayStr),
        employees: (employees || []).map((e: any) => ({ ID: e.ID, Name: e.Name, Teams: e.Teams || (e.Team ? [ e.Team ] : []) })),
        rocks: [ {
            rockID, name: String(form.Name || '').trim(), doneStatement: form.DoneStatement || null, ownerEmployeeID: form.OwnerEmployeeID || null, teamRock: !!form.TeamRock,
            existingMilestones: (form.Milestones || []).filter((m: any) => String(m.Text || '').trim())
                .map((m: any) => ({ text: m.Text, dueDate: m.DueDate, done: !!m.Done, ownerEmployeeID: m.OwnerEmployeeID || null }))
        } ]
    };
}

export interface DraftedMilestone { text: string, ownerEmployeeID: number | null, dueDate: string, keep: boolean }

// The rock's entry in the draft (the only one asked for), its milestones cleaned: text required and not one the rock
// already has; owner someone on the list, else the rock's owner (null); date between today and the quarter's end,
// else the quarter's end. Each starts ticked for the checkboxes.
export function draftedMilestones(draft: any, rockID: number, form: any, quarter: string, employees: any[], todayStr: string = today()): DraftedMilestone[] {
    const b = quarterBounds(quarter), earliest = earliestDue(quarter, todayStr);
    const rocks: any[] = Array.isArray(draft?.rocks) ? draft.rocks : [];
    const entry = rocks.find((r: any) => Number(r?.rockID) == rockID) || rocks[0] || {};
    const ids = new Set((employees || []).map((e: any) => Number(e.ID)));
    const have = new Set((form.Milestones || []).map((m: any) => String(m.Text || '').trim().toLowerCase()).filter((t: string) => t));
    const out: DraftedMilestone[] = [];
    for(const ms of (Array.isArray(entry.milestones) ? entry.milestones : [])) {
        const text = String(ms?.text || '').trim();
        if(!text || have.has(text.toLowerCase())) continue;
        have.add(text.toLowerCase());
        const owner = ms.ownerEmployeeID != null && ids.has(Number(ms.ownerEmployeeID)) ? Number(ms.ownerEmployeeID) : null;
        const dueDate = validDate(ms.dueDate) && ms.dueDate >= earliest && ms.dueDate <= b.end ? ms.dueDate : b.end;
        out.push({ text, ownerEmployeeID: owner, dueDate, keep: true });
    }
    return out;
}
